import type { ScholarshipModel, ScholarshipPlayer } from '../../../../types/scholarships';
import { formatMoney } from './scholarshipMoney';

export interface FormattedPercent {
  text: string;
  isWalkOn: boolean;
}

const MISSING: FormattedPercent = { text: '—', isWalkOn: false };

export const formatPlayerPercent = (player: ScholarshipPlayer): FormattedPercent => {
  if (player.scholarshipPercent == null) return MISSING;
  if (player.scholarshipPercent === 0) return { text: '0%', isWalkOn: true };
  return { text: `${player.scholarshipPercent}%`, isWalkOn: false };
};

// Replicates the server's MidpointRounding.AwayFromZero so the modal
// preview matches the post-save server value exactly.
const roundHalfUp = (n: number): number => {
  if (n >= 0) return Math.floor(n + 0.5);
  return -Math.floor(-n + 0.5);
};

export const formatPlayerPercentFromRaw = (
  cost: number | null,
  award: number | null,
): FormattedPercent => {
  if (cost == null || cost <= 0) return MISSING;
  const awardValue = award ?? 0;
  const rounded = roundHalfUp((awardValue / cost) * 100);
  if (rounded === 0) return { text: '0%', isWalkOn: true };
  return { text: `${rounded}%`, isWalkOn: false };
};

export const formatGroupAvgPercent = (players: ScholarshipPlayer[]): FormattedPercent => {
  let sumAward = 0;
  let sumCost = 0;
  for (const p of players) {
    if (p.cost == null || p.cost <= 0) continue;
    sumCost += p.cost;
    sumAward += p.award ?? 0;
  }
  if (sumCost <= 0) return MISSING;
  const rounded = roundHalfUp((sumAward / sumCost) * 100);
  if (rounded === 0) return { text: '0%', isWalkOn: true };
  return { text: `${rounded}%`, isWalkOn: false };
};

// FullHalf scholarships carry their value in scholarshipUnit (1.0 / 0.5), not a
// cost/award-derived percent. Mirrors the players table's formatUnit so the two
// views read identically.
const formatUnitValue = (n: number | null): string => (n == null ? '—' : n.toFixed(1));

export const formatPlayerUnit = (player: ScholarshipPlayer): FormattedPercent => ({
  text: formatUnitValue(player.scholarshipUnit),
  isWalkOn: false,
});

// Units sum (like the summary card's "Used" total), so the group aggregate is a
// sum, not an average. "—" when no player in the group has a unit assigned.
export const formatGroupTotalUnits = (players: ScholarshipPlayer[]): FormattedPercent => {
  let sum = 0;
  let any = false;
  for (const p of players) {
    if (p.scholarshipUnit == null) continue;
    sum += p.scholarshipUnit;
    any = true;
  }
  return any ? { text: sum.toFixed(1), isWalkOn: false } : MISSING;
};

// Total money a player is actually given across every bucket (award + NIL +
// rev share). The same composition as the players-tab "Total Package" column.
//
// Academic aid is NOT in here, and must not be: this figure feeds equivalency maths and the
// athletic aggregates, and academic aid does not count against athletic equivalency. See
// depthChartPlayerMoney for the one place the two are added together, and why.
export const totalPlayerMoney = (player: ScholarshipPlayer): number =>
  (player.award ?? 0) + (player.nilAmount ?? 0) + (player.revShare ?? 0);

/**
 * What a player is getting in total, for the depth chart's own display only.
 *
 * Athletic money plus academic aid. The coaches asked for this in one number: "they wanna be able
 * to visually see how much money a player is getting, and if they're like wow this guy's getting
 * $75,000 they don't really care how he's getting that money — they may then be like, his
 * production isn't there, we can take away some athletic money."
 *
 * It exists as a SEPARATE function from `totalPlayerMoney` rather than as a change to it, because
 * everything else that reads a player's money is a budget measure: the athletic allocation, the
 * average award, every per-position cap. Folding academic aid into those would report a roster as
 * over budget on money that never touched the budget.
 *
 * Used only where the depth chart already shows a DOLLAR figure. A percentage is an equivalency
 * against cost, and academic aid is not part of that — adding it to a percent would be arithmetic
 * on two different things.
 */
export const depthChartPlayerMoney = (player: ScholarshipPlayer): number =>
  totalPlayerMoney(player) + (player.academicAmount ?? 0);

/**
 * Whether the depth chart's figure for this player is a dollar total rather than a percent or a
 * unit — which is to say, whether academic aid has been added into it.
 *
 * Exposed so the row can explain the number it is showing instead of sniffing the formatted text
 * for a leading "$".
 */
export const depthChartValueIsMoney = (
  player: ScholarshipPlayer,
  model: ScholarshipModel,
): boolean =>
  model === 'FullHalf' ? player.scholarshipUnit == null : player.scholarshipPercent == null;

// Per-player value for the depth chart. The card shows one number, so when the
// model's primary measure is absent we surface the money the player is given
// (award + NIL + rev share) rather than leaving the card blank — a money-only
// recruit (NIL or straight dollars, no scholarship %) still reads as a dollar
// figure. FullHalf shows its unit; percent models show the %; otherwise the
// total money; "—" only when there is truly nothing.
export const formatPlayerValue = (
  player: ScholarshipPlayer,
  model: ScholarshipModel,
): FormattedPercent => {
  if (model === 'FullHalf') {
    if (player.scholarshipUnit != null) return formatPlayerUnit(player);
    const money = depthChartPlayerMoney(player);
    return money > 0 ? { text: formatMoney(money), isWalkOn: false } : MISSING;
  }
  // A percent is an equivalency against cost. Academic aid is not part of that, so it stays its
  // own chip on the row rather than being added into a number it does not belong in.
  if (player.scholarshipPercent != null) return formatPlayerPercent(player);
  const money = depthChartPlayerMoney(player);
  return money > 0 ? { text: formatMoney(money), isWalkOn: false } : MISSING;
};

export interface GroupAggregate extends FormattedPercent {
  label: string;
}

// Total-money sum (award + NIL + rev share) for a group where no player has a
// derivable percent — the depth chart header, matching its per-card money.
// "—" when no player carries any money.
const formatGroupTotalMoney = (players: ScholarshipPlayer[]): FormattedPercent => {
  let sum = 0;
  let any = false;
  for (const p of players) {
    // Academic aid included, because this header exists to match the money on the cards under it
    // and those now include it. This is a display total, not one of the budget aggregates — the
    // Totals/Average toggle below is the budget measure, and it deliberately excludes academic.
    const money = depthChartPlayerMoney(p);
    if (money <= 0) continue;
    sum += money;
    any = true;
  }
  return any ? { text: formatMoney(sum), isWalkOn: false } : MISSING;
};

// Group header: "Units {sum}" for FullHalf; otherwise "Avg {pct}" when any
// player has a derivable percent (mixed groups keep the avg-over-costed-players
// behavior). `moneyMode` controls the no-percent fallback: 'total' (depth chart)
// shows "Total {$Σ all buckets}" so the header matches the per-card money;
// 'none' (players tab) shows "—" — the players tab has dedicated money columns,
// and a scholarship-% header shouldn't invent a figure where there's no cost.
export const formatGroupAggregate = (
  players: ScholarshipPlayer[],
  model: ScholarshipModel,
  moneyMode: 'none' | 'total' = 'none',
): GroupAggregate => {
  if (model === 'FullHalf') {
    return { label: 'Units', ...formatGroupTotalUnits(players) };
  }
  const hasPercent = players.some((p) => p.cost != null && p.cost > 0);
  if (hasPercent) {
    return { label: 'Avg', ...formatGroupAvgPercent(players) };
  }
  if (moneyMode === 'total') {
    const totals = formatGroupTotalMoney(players);
    if (totals.text !== '—') return { label: 'Total', ...totals };
  }
  return { label: 'Avg', ...MISSING };
};

// Model-aware group sum + funded-player count, used by the depth-chart Totals/Average
// toggle. "Funded" mirrors the server calculator: FullHalf unit>0, Equivalency cost>0,
// Percentage award>0. Sum is in the model's unit (units / equivalency / dollars).
const groupSumAndFunded = (
  players: ScholarshipPlayer[],
  model: ScholarshipModel,
): { sum: number; funded: number } => {
  let sum = 0;
  let funded = 0;
  for (const p of players) {
    if (model === 'FullHalf') {
      const u = p.scholarshipUnit ?? 0;
      sum += u;
      if (u > 0) funded += 1;
    } else if (model === 'Equivalency') {
      if (p.cost != null && p.cost > 0) {
        sum += (p.award ?? 0) / p.cost;
        funded += 1;
      }
    } else {
      const a = p.award ?? 0;
      sum += a;
      if (a > 0) funded += 1;
    }
  }
  return { sum, funded };
};

export type DepthChartDisplayMode = 'total' | 'average';

// Depth-chart card header under the global Totals/Average toggle. TOTAL shows the group
// sum; AVERAGE shows sum ÷ funded count (matching the summary-band average). Both render
// in the model's unit — dollars for Percentage, one-decimal counts for FullHalf/Equivalency.
export const formatGroupToggleAggregate = (
  players: ScholarshipPlayer[],
  model: ScholarshipModel,
  displayMode: DepthChartDisplayMode,
): GroupAggregate => {
  const { sum, funded } = groupSumAndFunded(players, model);
  const fmt = (v: number): string =>
    model === 'Percentage' ? formatMoney(v) : v.toFixed(1);

  if (displayMode === 'average') {
    if (funded === 0) return { label: 'Avg', text: '—', isWalkOn: false };
    const avg = sum / funded;
    return { label: 'Avg', text: fmt(avg), isWalkOn: avg <= 0 };
  }
  if (funded === 0 && sum <= 0) return { label: 'Total', text: '—', isWalkOn: false };
  return { label: 'Total', text: fmt(sum), isWalkOn: sum <= 0 };
};
